// navyManual.ts
import { oklchHex, rgbToOklch } from '../generator/oklch';
import type { TokenMap } from '../generator/types';

/**
 * Hand-curated navy brand theme — the one brand that is NOT ramp-generated.
 *
 * Why it exists: `ramp.ts` normalizes every brand fill to a fixed lightness
 * (L≈0.55) and scales the ramp by the seed's CHROMA only — the seed's own
 * lightness is discarded. For the real navy seed `#004268` (OKLCH L 0.364)
 * that lifts the accent to a washed-out `#2d73bf` baby blue, which is why the
 * generated `navy` theme has to smuggle the navy character in through a
 * brand-tinted NEUTRAL instead (see `brandThemes.navy` in `defaultSeeds.ts`).
 *
 * This theme instead pins the seed 1:1 as the light-mode `brand-bg` and lays
 * out every other step by hand, all on the seed's exact OKLCH hue (242.75°),
 * with chroma tapering as lightness rises. No neutral workaround needed: the
 * greys sit on the brand hue directly.
 *
 * The values below are OKLCH L/C pairs, not hex literals, so the theme stays
 * tunable — change an L/C, re-run `yarn generate`, done. Roles NOT listed here
 * (danger/success/warning fills, the white-alpha nav overlays) are hue-neutral
 * and inherited from the base theme by `emitCss`.
 *
 * Dark mode cannot keep the seed 1:1 — `#004268` on a dark surface is 1.3:1,
 * far below AA — so the brand fill is lifted to L 0.585 while keeping the seed
 * hue and its chroma relation. Everything else follows the same hue.
 */
export const NAVY_MANUAL_SEED = '#004268';

/** The seed's own OKLCH hue (≈242.75°) — every step below sits on it. */
const H = rgbToOklch(NAVY_MANUAL_SEED).H;

/** One navy step: OKLCH lightness + chroma on the seed hue, gamut-clipped to sRGB. */
const step = (L: number, C: number): string => oklchHex(L, C, H);

/** rgba() from a hex — used for the soft focus ring and the modal overlay. */
function alpha(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

/* ── Light ────────────────────────────────────────────────────────────────
   Surfaces stay near-white with a faint navy cast (C 0.008–0.035); text and
   fills carry the seed's chroma. The brand fill IS the seed; hover/active go
   deeper (same direction the generator uses in light mode). */
const L = {
  sunken: step(0.986, 0.008),
  border: step(0.925, 0.018),
  borderStrong: step(0.815, 0.035),
  borderSubtle: step(0.962, 0.012),
  fg: step(0.29, 0.045),
  fgSubtle: step(0.475, 0.055),
  fgMuted: step(0.6, 0.048),
  disabledFg: step(0.775, 0.03),
  inputBg: step(0.968, 0.01),
  inputBgHover: step(0.938, 0.016),
  brand: NAVY_MANUAL_SEED,
  brandHover: step(0.318, 0.092),
  brandActive: step(0.268, 0.082),
  brandSubtle: step(0.975, 0.014),
  brandSubtleHover: step(0.945, 0.032),
  brandSubtleFg: step(0.325, 0.082),
  brandBorder: step(0.68, 0.088),
  neutral: step(0.545, 0.045),
  neutralHover: step(0.475, 0.048),
  neutralActive: step(0.405, 0.048),
  neutralBorder: step(0.685, 0.035),
  navBg: step(0.275, 0.07),
  navAccent: step(0.735, 0.115),
  tableRowHover: step(0.938, 0.02),
  overlay: step(0.3, 0.06),
};

/* ── Dark ─────────────────────────────────────────────────────────────────
   Surfaces are navy-tinted near-blacks; the brand fill is lifted out of the
   seed's depth so it clears AA against them, and the subtle fills stay a
   readable navy instead of the generated near-black. */
const D = {
  surface: step(0.215, 0.012),
  raised: step(0.265, 0.018),
  sunken: step(0.175, 0.012),
  border: step(0.345, 0.03),
  borderStrong: step(0.475, 0.042),
  fg: step(0.945, 0.022),
  fgSubtle: step(0.775, 0.038),
  fgMuted: step(0.645, 0.042),
  disabledFg: step(0.475, 0.03),
  inputBgHover: step(0.305, 0.022),
  brand: step(0.585, 0.125),
  brandHover: step(0.645, 0.125),
  brandActive: step(0.705, 0.112),
  brandSubtle: step(0.245, 0.042),
  brandSubtleHover: step(0.295, 0.052),
  brandSubtleFg: step(0.905, 0.048),
  brandBorder: step(0.445, 0.095),
  neutralHover: step(0.715, 0.04),
  navBg: step(0.225, 0.055),
  navAccent: step(0.735, 0.115),
  tableRowActive: step(0.285, 0.048),
  overlay: step(0.16, 0.045),
};

/** Light-mode role overrides on top of the base light theme. */
export const navyManualLight: TokenMap = {
  'surface-bg': '#ffffff',
  'surface-raised-bg': '#ffffff',
  'surface-sunken-bg': L.sunken,
  'bg-app': L.sunken,
  border: L.border,
  'border-strong': L.borderStrong,
  'border-subtle': L.borderSubtle,
  fg: L.fg,
  'fg-subtle': L.fgSubtle,
  'fg-muted': L.fgMuted,
  'disabled-bg': L.borderSubtle,
  'disabled-fg': L.disabledFg,
  'input-bg': L.inputBg,
  'input-bg-hover': L.inputBgHover,
  'overlay-bg': alpha(L.overlay, 0.42),
  'brand-bg': L.brand,
  'brand-bg-hover': L.brandHover,
  'brand-bg-active': L.brandActive,
  'brand-bg-focus': L.brand,
  'brand-bg-disabled': L.borderSubtle,
  'brand-fg-disabled': L.disabledFg,
  'brand-subtle-bg': L.brandSubtle,
  'brand-subtle-bg-hover': L.brandSubtleHover,
  'brand-subtle-fg': L.brandSubtleFg,
  'brand-border': L.brandBorder,
  'brand-border-hover': L.brandHover,
  'brand-border-active': L.brandActive,
  'neutral-bg': L.neutral,
  'neutral-bg-hover': L.neutralHover,
  'neutral-bg-active': L.neutralActive,
  'neutral-bg-focus': L.neutral,
  'neutral-bg-disabled': L.borderSubtle,
  'neutral-fg-disabled': L.disabledFg,
  'neutral-subtle-bg': L.sunken,
  'neutral-subtle-bg-hover': L.borderSubtle,
  'neutral-subtle-fg': L.fg,
  'neutral-border': L.neutralBorder,
  'neutral-border-hover': L.neutralHover,
  'neutral-border-active': L.neutralActive,
  'danger-bg-disabled': L.borderSubtle,
  'danger-fg-disabled': L.disabledFg,
  'success-bg-disabled': L.borderSubtle,
  'success-fg-disabled': L.disabledFg,
  'warning-bg-disabled': L.borderSubtle,
  'warning-fg-disabled': L.disabledFg,
  'info-bg': L.brand,
  'info-bg-hover': L.brandHover,
  'info-bg-active': L.brandActive,
  'info-bg-focus': L.brand,
  'info-bg-disabled': L.borderSubtle,
  'info-fg-disabled': L.disabledFg,
  'info-subtle-bg': L.brandSubtle,
  'info-subtle-bg-hover': L.brandSubtleHover,
  'info-subtle-fg': L.brandSubtleFg,
  'info-border': L.brandBorder,
  'info-border-hover': L.brandHover,
  'info-border-active': L.brandActive,
  'focus-ring': L.brand,
  'focus-ring-soft': alpha(L.brand, 0.32),
  'nav-bg': L.navBg,
  'nav-accent': L.navAccent,
  'table-header-bg': L.border,
  'table-header-fg': L.fgSubtle,
  'table-zebra-bg': L.inputBg,
  'table-row-bg': '#ffffff',
  'table-row-hover-bg': L.tableRowHover,
  'table-row-active-bg': L.brandSubtleHover,
  'table-border': L.border,
  'table-filter-bg': L.inputBg,
  link: L.brand,
  'link-hover': L.brandHover,
  'link-active': L.brandActive,
};

/** Dark-mode role overrides on top of the base dark theme. */
export const navyManualDark: TokenMap = {
  'surface-bg': D.surface,
  'surface-raised-bg': D.raised,
  'surface-sunken-bg': D.sunken,
  'bg-app': D.sunken,
  border: D.border,
  'border-strong': D.borderStrong,
  'border-subtle': D.raised,
  fg: D.fg,
  'fg-subtle': D.fgSubtle,
  'fg-muted': D.fgMuted,
  'disabled-bg': D.raised,
  'disabled-fg': D.disabledFg,
  'input-bg': D.raised,
  'input-bg-hover': D.inputBgHover,
  'overlay-bg': alpha(D.overlay, 0.62),
  'brand-bg': D.brand,
  'brand-bg-hover': D.brandHover,
  'brand-bg-active': D.brandActive,
  'brand-bg-focus': D.brand,
  'brand-bg-disabled': D.raised,
  'brand-fg-disabled': D.disabledFg,
  'brand-subtle-bg': D.brandSubtle,
  'brand-subtle-bg-hover': D.brandSubtleHover,
  'brand-subtle-fg': D.brandSubtleFg,
  'brand-border': D.brandBorder,
  'brand-border-hover': D.brandHover,
  'brand-border-active': D.brandActive,
  'neutral-bg': D.fgMuted,
  'neutral-bg-hover': D.neutralHover,
  'neutral-bg-active': D.fgSubtle,
  'neutral-bg-focus': D.fgMuted,
  'neutral-bg-disabled': D.raised,
  'neutral-fg-disabled': D.disabledFg,
  'neutral-subtle-bg': D.sunken,
  'neutral-subtle-bg-hover': D.surface,
  'neutral-subtle-fg': D.fg,
  'neutral-border': D.borderStrong,
  'neutral-border-hover': D.neutralHover,
  'neutral-border-active': D.fgSubtle,
  'danger-bg-disabled': D.raised,
  'danger-fg-disabled': D.disabledFg,
  'success-bg-disabled': D.raised,
  'success-fg-disabled': D.disabledFg,
  'warning-bg-disabled': D.raised,
  'warning-fg-disabled': D.disabledFg,
  'info-bg': D.brand,
  'info-bg-hover': D.brandHover,
  'info-bg-active': D.brandActive,
  'info-bg-focus': D.brand,
  'info-bg-disabled': D.raised,
  'info-fg-disabled': D.disabledFg,
  'info-subtle-bg': D.brandSubtle,
  'info-subtle-bg-hover': D.brandSubtleHover,
  'info-subtle-fg': D.brandSubtleFg,
  'info-border': D.brandBorder,
  'info-border-hover': D.brandHover,
  'info-border-active': D.brandActive,
  'focus-ring': D.brandHover,
  'focus-ring-soft': alpha(D.brandHover, 0.5),
  'nav-bg': D.navBg,
  'nav-accent': D.navAccent,
  'table-header-bg': D.raised,
  'table-header-fg': D.fgSubtle,
  'table-zebra-bg': D.sunken,
  'table-row-bg': D.surface,
  'table-row-hover-bg': D.inputBgHover,
  'table-row-active-bg': D.tableRowActive,
  'table-border': D.border,
  'table-filter-bg': D.raised,
  link: D.brand,
  'link-hover': D.brandHover,
  'link-active': D.brandActive,
};

/**
 * Hand-curated brand themes. They skip the ramp and ship as runtime
 * catalog token maps (navy-manual). Connect injects them unlayered —
 * they are not emitted as `[data-brand]` blocks.
 */
export const manualBrandThemes: Record<string, { light: TokenMap; dark: TokenMap }> = {
  'navy-manual': { light: navyManualLight, dark: navyManualDark },
};
